// Types
import { FeedbackData } from '@/components/EmployeeYearInReview/scenes/Scene4/types';
import { TrainingFeedbackData } from '@/components/EmployeeYearInReview/scenes/Scene5c/types';
import {
  EmployeeCertification,
  EmployeeTraining,
  Mentee,
  Supportee,
} from '@/types';

// Utils
import { calculateScene3bDuration } from '@/components/EmployeeYearInReview/scenes/Scene3b/utils';
import {
  calculateTotalScene4Duration,
  getRecentFeedbacks,
} from '@/components/EmployeeYearInReview/scenes/Scene4/utils';
import { calculateScene2Duration } from '@/components/EmployeeYearInReview/scenes/Scene2/utils';
import { calculateScene5bDuration } from '@/components/EmployeeYearInReview/scenes/Scene5b/utils';
import { calculateTotalScene6Duration } from '@/components/EmployeeYearInReview/scenes/Scene6/utils';
import {
  calculateTotalScene7Duration,
  getTopSupportersFromTrainings,
  convertMenteesToTeamMembers,
  convertSupporteesToTeamMembers,
} from '@/components/EmployeeYearInReview/scenes/Scene7/utils';
import {
  calculateTotalScene5cDuration,
  getRecentTrainingFeedbacks,
} from '@/components/EmployeeYearInReview/scenes/Scene5c/utils';

// Constants
import {
  EMPLOYEE_YEAR_IN_REVIEW_CONFIG,
  SCENE_3A_3B_OVERLAP_FRAMES,
  DIAGONAL_WIPE_EXPAND_DURATION,
  DIAGONAL_WIPE_HOLD_DURATION,
  DIAGONAL_WIPE_COLLAPSE_DURATION,
  DIAGONAL_WIPE_TOTAL_DURATION,
  TRANSITION_OVERLAP,
  ZOOM_TRANSITION_DURATION,
  SCENE_5A_5B_OVERLAP,
  SCENE_5B_5C_OVERLAP,
  SCENE_5_DURATION,
  SCENE_6_SLIDE_IN_DURATION,
  CROSS_ZOOM_TRANSITION_DURATION,
  SCENE_8_DURATION,
} from '@/constants';

export interface SceneTransitionFrames {
  scene2End: number;
  scene3bStart: number;
  diagonalWipeStartLocal: number;
  scene4Start: number;
  scene4End: number;
  scene5Start: number;
  scene5aStart: number;
  scene5aEnd: number;
  scene5bStart: number;
  scene5bEnd: number;
  scene5cStart: number;
  scene5cEnd: number;
  scene6Start: number;
  scene7Start: number;
}

const calculateScene5To6Frames = (
  scene5aStart: number,
  scene5bDuration: number,
  scene5aDuration: number,
  scene5cDuration: number = 0,
  scene6Duration: number = 0,
) => {
  // No trainings: Scene 5 (empty) replaces 5a/5b/5c
  // Scene 6 overlaps Scene 5 end for slide-right transition (if Scene 6 exists)
  if (scene5aDuration <= 0 && scene5bDuration <= 0) {
    const scene5End = scene5aStart + SCENE_5_DURATION;
    const hasScene6 = scene6Duration > 0;
    return {
      scene5Start: scene5aStart,
      scene5aEnd: scene5aStart,
      scene5bStart: scene5aStart,
      scene5bEnd: scene5aStart,
      scene5cStart: scene5aStart,
      scene5cEnd: scene5aStart,
      scene6Start: hasScene6
        ? scene5End - SCENE_6_SLIDE_IN_DURATION
        : scene5End,
    };
  }

  const scene5aEnd = scene5aStart + scene5aDuration;

  // Scene5a exists but no scene5b: skip scene5b and scene5c
  if (scene5bDuration <= 0) {
    return {
      scene5Start: scene5aStart,
      scene5aEnd,
      scene5bStart: scene5aEnd,
      scene5bEnd: scene5aEnd,
      scene5cStart: scene5aEnd,
      scene5cEnd: scene5aEnd,
      scene6Start: scene5aEnd,
    };
  }

  // Both Scene5a and Scene5b exist: overlap transition, then Scene5c
  const scene5bStart = scene5aEnd - SCENE_5A_5B_OVERLAP;
  const scene5bEnd = scene5bStart + scene5bDuration;
  const scene5cStart = scene5bEnd - SCENE_5B_5C_OVERLAP;
  const scene5cEnd = scene5cStart + scene5cDuration;
  const scene6Start =
    scene5cDuration > 0 ? scene5cEnd - SCENE_6_SLIDE_IN_DURATION : scene5cEnd;

  return {
    scene5Start: scene5aStart,
    scene5aEnd,
    scene5bStart,
    scene5bEnd,
    scene5cStart,
    scene5cEnd,
    scene6Start,
  };
};

export const getSceneTransitionFrames = (
  scene2Duration: number,
  scene3bDuration: number,
  scene4Duration: number,
  scene5bDuration: number,
  scene5cDuration: number = 0,
  scene6Duration?: number,
  scene7Duration?: number,
): SceneTransitionFrames => {
  const { timing } = EMPLOYEE_YEAR_IN_REVIEW_CONFIG;
  const actualScene5aDuration =
    scene5bDuration > 0 ? timing.slide5a.duration : 0;
  const hasScene7 = (scene7Duration ?? timing.slide7.duration) > 0;
  const effectiveScene6Duration = scene6Duration ?? timing.slide6a.duration;

  const scene2End = timing.slide2.start + scene2Duration;

  // If no projects (scene3bDuration === 0), skip scenes 3a, 3b, and 4 entirely
  // Transition directly from Scene 2 to Scene 5a
  if (scene3bDuration === 0) {
    // Scene 5a slides in from right, starting during Scene 2's slide out
    const scene5aStart = scene2End - TRANSITION_OVERLAP;
    const scene5And6Frames = calculateScene5To6Frames(
      scene5aStart,
      scene5bDuration,
      actualScene5aDuration,
      scene5cDuration,
      effectiveScene6Duration,
    );
    const scene7Start =
      scene5And6Frames.scene6Start +
      effectiveScene6Duration -
      (hasScene7 ? CROSS_ZOOM_TRANSITION_DURATION : 0);

    return {
      scene2End,
      scene3bStart: 0,
      diagonalWipeStartLocal: 0,
      scene4Start: 0,
      scene4End: 0,
      scene5aStart,
      ...scene5And6Frames,
      scene7Start,
    };
  }

  const scene3aStart = scene2End - TRANSITION_OVERLAP;
  const scene3aEnd = scene3aStart + timing.slide3a.duration;
  const scene3bStart = scene3aEnd - SCENE_3A_3B_OVERLAP_FRAMES;

  const diagonalWipeStartLocal =
    scene3bDuration + SCENE_3A_3B_OVERLAP_FRAMES - DIAGONAL_WIPE_TOTAL_DURATION;

  // If projects exist but no feedback (scene4Duration === 0), skip Scene 4
  // Scene 5a is revealed directly under the diagonal wipe
  if (scene4Duration === 0) {
    const scene5aStart =
      scene3bStart +
      diagonalWipeStartLocal +
      DIAGONAL_WIPE_EXPAND_DURATION +
      DIAGONAL_WIPE_HOLD_DURATION;
    const scene5And6Frames = calculateScene5To6Frames(
      scene5aStart,
      scene5bDuration,
      actualScene5aDuration,
      scene5cDuration,
      effectiveScene6Duration,
    );
    const scene7Start =
      scene5And6Frames.scene6Start +
      effectiveScene6Duration -
      (hasScene7 ? CROSS_ZOOM_TRANSITION_DURATION : 0);

    return {
      scene2End,
      scene3bStart,
      diagonalWipeStartLocal,
      scene4Start: 0,
      scene4End: 0,
      scene5aStart,
      ...scene5And6Frames,
      scene7Start,
    };
  }

  // Both projects and feedback exist
  const scene4Start =
    scene3bStart +
    diagonalWipeStartLocal +
    DIAGONAL_WIPE_EXPAND_DURATION +
    DIAGONAL_WIPE_HOLD_DURATION;
  const scene4End =
    scene4Start + scene4Duration + DIAGONAL_WIPE_COLLAPSE_DURATION;
  const scene5aStart = scene4End - ZOOM_TRANSITION_DURATION;
  const scene5And6Frames = calculateScene5To6Frames(
    scene5aStart,
    scene5bDuration,
    actualScene5aDuration,
    scene5cDuration,
    effectiveScene6Duration,
  );
  const scene7Start =
    scene5And6Frames.scene6Start +
    effectiveScene6Duration -
    (hasScene7 ? CROSS_ZOOM_TRANSITION_DURATION : 0);

  return {
    scene2End,
    scene3bStart,
    diagonalWipeStartLocal,
    scene4Start,
    scene4End,
    scene5aStart,
    ...scene5And6Frames,
    scene7Start,
  };
};

// Total composition length (dynamic scene lengths)
export const calculateEmployeeYearInReviewDuration = (
  scene3bDuration: number,
  scene4Duration: number,
  scene5bDuration?: number,
  scene5cDuration?: number,
  scene2Duration?: number,
  scene6Duration?: number,
  scene7Duration?: number,
): number => {
  const { timing } = EMPLOYEE_YEAR_IN_REVIEW_CONFIG;
  const scene2 = scene2Duration ?? timing.slide2.duration;
  const scene5b = scene5bDuration ?? timing.slide5b.duration;
  const effectiveScene5c = scene5cDuration ?? 0;
  const effectiveScene6 = scene6Duration ?? timing.slide6a.duration;
  const scene7ContentDuration = scene7Duration ?? timing.slide7.duration;
  const { scene7Start } = getSceneTransitionFrames(
    scene2,
    scene3bDuration,
    scene4Duration,
    scene5b,
    effectiveScene5c,
    effectiveScene6,
    scene7ContentDuration,
  );
  const scene8Start =
    scene7ContentDuration > 0
      ? scene7Start + scene7ContentDuration + CROSS_ZOOM_TRANSITION_DURATION
      : scene7Start;

  return scene8Start + SCENE_8_DURATION;
};

export const calculateEmployeeYearInReviewMetadata = async ({
  props,
}: {
  props: {
    projects?: { length: number };
    projectFeedbacks?: FeedbackData[];
    trainings?: EmployeeTraining[];
    trainingFeedbacks?: TrainingFeedbackData[];
    employee?: {
      info?: { onboardedAt?: string };
      certs?: EmployeeCertification[];
    };
    mentees?: Mentee[];
    supportees?: Supportee[];
  };
}) => {
  const projectsCount = props.projects?.length ?? 0;
  const allFeedbacks = props.projectFeedbacks ?? [];
  const onboardedAt = props.employee?.info?.onboardedAt ?? '';
  const trainings = props.trainings ?? [];
  const allTrainingFeedbacks = props.trainingFeedbacks ?? [];
  const certificates = props.employee?.certs ?? [];
  const mentees = props.mentees ?? [];
  const supportees = props.supportees ?? [];

  // Filter to max 3 feedbacks
  const displayFeedbacks = getRecentFeedbacks(allFeedbacks);

  // Filter to max 3 training feedbacks
  const displayTrainingFeedbacks =
    getRecentTrainingFeedbacks(allTrainingFeedbacks);

  // Calculate dynamic durations based on actual data
  const scene2Duration = calculateScene2Duration(onboardedAt);
  const scene3bDuration = calculateScene3bDuration(projectsCount);
  const scene4Duration = calculateTotalScene4Duration(displayFeedbacks);
  const scene5bDuration = calculateScene5bDuration(trainings);
  const scene5cDuration = calculateTotalScene5cDuration(
    displayTrainingFeedbacks,
  );
  const scene6Duration = calculateTotalScene6Duration(certificates);
  const topSupporters = getTopSupportersFromTrainings(trainings);
  const menteeMembers = convertMenteesToTeamMembers(mentees);
  const supporteeMembers = convertSupporteesToTeamMembers(supportees);
  const scene7Duration = calculateTotalScene7Duration([
    topSupporters.length,
    menteeMembers.length,
    supporteeMembers.length,
  ]);

  const totalDuration = calculateEmployeeYearInReviewDuration(
    scene3bDuration,
    scene4Duration,
    scene5bDuration,
    scene5cDuration,
    scene2Duration,
    scene6Duration,
    scene7Duration,
  );

  return {
    durationInFrames: totalDuration,
  };
};
